import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";
const TermsOfUse = () => {
  useEffect(()=>{
    logEvent(analytics, 'terms_of_use_page');
  },[])
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 text-gray-800 dark:text-gray-200">
      <Helmet>
        <title>Terms of Use | AI Contract Manager</title>
        <meta
          name="description"
          content="Review the Terms of Use for AI Contract Manager, the secure and AI-powered solution for contract extraction, analysis, and management."
        />
        <link rel="canonical" href="https://aicontractmanager.com/terms" />
        <meta property="og:title" content="Terms of Use | AI Contract Manager" />
        <meta
          property="og:description"
          content="Understand the terms and conditions for using the AI Contract Manager platform, owned and operated by Clairvoyant Inc."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aicontractmanager.com/terms" />
      </Helmet>
      <h1 className="text-3xl font-bold mb-6 text-center">Terms of Use</h1>
      <p className="text-sm mb-6 text-center">Effective Date: April 2, 2025</p>

      <p className="mb-4">
        This Terms of Use Agreement (“Terms”) governs your use of the AI Contract Management App (the “App”), the website under the domain name www.aicontractmanager.com (the “Site”), and related services accessible through the App and Site (collectively, the “Services”) offered by <strong>Clairvoyant Inc.</strong> (“Clairvoyant,” “Company,” “we,” or “us”).
      </p>

      <p className="mb-4">
        BY ACCESSING OR USING THE SITE, APP, OR SERVICES, YOU AGREE THAT (1) YOU HAVE READ AND UNDERSTAND THESE TERMS; (2) YOU ARE OF LEGAL AGE TO FORM A BINDING CONTRACT; AND (3) YOU HAVE THE AUTHORITY TO ENTER INTO THESE TERMS. IF YOU DO NOT AGREE, YOU MAY NOT USE THE SERVICES.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-2">1. Service Description</h2>
      <p className="mb-4">The App is a contract management tool that uses AI to extract and summarize key information from uploaded documents. The Services are provided for informational purposes only and do not constitute legal advice.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">2. Privacy and Personal Information</h2>
      <p className="mb-4">You agree to our Privacy Policy, which governs how we collect and use your personal data. We may use anonymized, aggregated data to improve the Services.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">3. User Accounts</h2>
      <p className="mb-4">You must create an account to access certain Services. You are responsible for maintaining the confidentiality of your login credentials. You agree to provide accurate and current information and to update it as necessary.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">4. Identity Verification</h2>
      <p className="mb-4">You authorize Clairvoyant to verify your identity using any necessary means, including third-party services.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">5. Service Interruptions</h2>
      <p className="mb-4">The Services may be temporarily unavailable due to maintenance, updates, or other reasons. Clairvoyant is not liable for any such interruptions.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">6. Mobile Usage</h2>
      <p className="mb-4">Use of the Services on mobile devices may be subject to additional charges and terms from your provider. Clairvoyant is not responsible for your access to or use of the Services through such devices.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">7. Alerts and Notifications</h2>
      <p className="mb-4">We may send you alerts or notifications regarding your account. We are not responsible for any delays or delivery failures.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">8. Rights You Grant Us</h2>
      <p className="mb-4">By uploading documents or data, you grant us a license to use that content solely to provide the Services. You retain ownership of your data.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">9. Intellectual Property</h2>
      <p className="mb-4">All content in the App and Site is owned or licensed by Clairvoyant. You may not copy, modify, or distribute any part of the Services without our written consent.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">10. Prohibited Use</h2>
      <p className="mb-4">You agree not to use the Services for any unlawful purpose or in a way that could harm the App or its users.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">11. Feedback</h2>
      <p className="mb-4">If you submit feedback, you grant Clairvoyant a perpetual, worldwide license to use and incorporate it into the Services.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">12. Payments and Subscriptions</h2>
      <p className="mb-4">Access to the full features of the App requires a paid subscription. Subscriptions are billed at $9.99 USD per month, and are managed through Stripe, our third-party payment processor.

        By subscribing, you agree to the following:

        Your subscription will automatically renew each month unless canceled prior to your next billing cycle.

        You authorize us to charge your provided payment method each billing cycle via Stripe.

        You can cancel your subscription at any time from your account settings or by contacting support.

        No prorated refunds are provided for partial months. Upon cancellation, you will continue to have access until the end of your current billing period.

        Clairvoyant Inc. reserves the right to change pricing or payment terms with reasonable notice. Any such changes will not apply retroactively.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">13. Disclaimer of Warranties</h2>
      <p className="mb-4">THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." CLAIRVOYANT MAKES NO WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">14. Limitation of Liability</h2>
      <p className="mb-4">CLAIRVOYANT IS NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICES.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">15. Indemnification</h2>
      <p className="mb-4">You agree to indemnify Clairvoyant against any claims arising from your use of the Services or violation of these Terms.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">16. Modifications</h2>
      <p className="mb-4">We may update these Terms at any time. Continued use of the Services after changes constitutes acceptance.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">17. Termination</h2>
      <p className="mb-4">You may terminate your account at any time. Clairvoyant may suspend or terminate your access for violations of these Terms.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">18. Governing Law</h2>
      <p className="mb-4">These Terms are governed by California law. Disputes will be resolved in Orange County, California.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">19. Severability</h2>
      <p className="mb-4">If any part of these Terms is found unenforceable, the remaining provisions will remain in full force.</p>

      <h2 className="text-xl font-semibold mt-8 mb-2">20. Entire Agreement</h2>
      <p className="mb-4">These Terms constitute the entire agreement between you and Clairvoyant regarding the Services.</p>

      <p className="mt-6">For questions, contact us at <a href="mailto:support@aicontractmanager.com" className="text-blue-600 dark:text-blue-400 underline">support@aicontractmanager.com</a>.</p>
    </div>
  );
};

export default TermsOfUse;
